// HostelLoginPage.jsx - Shared login screen for Boys & Girls Hostel Management
// REAL backend login — checks the entered username/password against the
// Staff collection (dept: "Hostel", role: "Boys Hostel Warden" / "Girls
// Hostel Warden") via POST /api/staff/warden-login. Wardens are created from
// StaffManagement.js's "Add Warden" form. No hardcoded/demo credentials —
// only what's actually stored in the database is accepted.
// On successful login, navigates to the correct hostel dashboard route using
// react-router-dom (/hostel-boys or /hostel-girls). Make sure those routes
// are registered in App.js (see wiring notes at the bottom of this file).
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, ArrowRight, AlertTriangle, User, EyeOff } from "lucide-react";
const NAVY = "#0D47A1";
const BLUE = "#2196F3";
const LIGHT = "#90CAF9";
const PALE = "#E3F2FD";
const GOLD = "#C9A227";
const GOLD_LIGHT = "#F0D98C";
const API_BASE = "https://schoolcrm-fzcv.onrender.com/api/staff";

// Hostel choice cards — no passwords here anymore, just which portal to log into.
// hostelType is sent to the backend so a Boys warden's login can't be used on
// the Girls tab (or vice-versa) even if the username/password are correct.
const HOSTELS = {
  boys: {
    hostelType: "Boys",
    route: "/hostel-boys",
    label: "Boys Hostel",
    icon: <User className="inline w-[1em] h-[1em] align-[-0.125em]" />
  },
  girls: {
    hostelType: "Girls",
    route: "/hostel-girls",
    label: "Girls Hostel",
    icon: <User className="inline w-[1em] h-[1em] align-[-0.125em]" />
  }
};
export default function HostelLoginPage() {
  const navigate = useNavigate();
  const [role, setRole] = useState(null); // "boys" | "girls" | null
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  function pickRole(r) {
    setRole(r);
    setUsername("");
    setPassword("");
    setError("");
  }
  function backToRoleSelect() {
    setRole(null);
    setUsername("");
    setPassword("");
    setError("");
  }
  async function handleLogin(e) {
    e.preventDefault();
    setError("");
    if (!username.trim() || !password.trim()) {
      setError("Please enter both username and password.");
      return;
    }
    const hostel = HOSTELS[role];
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/warden-login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          username: username.trim(),
          password: password.trim(),
          hostelType: hostel.hostelType
        })
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.message || "Invalid username or password. Please try again.");
        setLoading(false);
        return;
      }

      // Success — small delay just for a nice loading feel, remove if not needed.
      setTimeout(() => navigate(hostel.route), 400);
    } catch (err) {
      console.error("Warden login error:", err);
      setError("Could not reach the server. Please check your connection and try again.");
      setLoading(false);
    }
  }
  return <div style={{
    minHeight: "100vh",
    background: `linear-gradient(135deg, ${NAVY} 0%, ${BLUE} 55%, ${LIGHT} 100%)`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
    padding: 20
  }}>
      <div style={{
      background: "#FFFFFF",
      borderRadius: 28,
      width: "100%",
      maxWidth: 440,
      boxShadow: "0 20px 60px rgba(13,71,161,0.35)",
      overflow: "hidden"
    }}>
        {/* Header */}
        <div style={{
        background: `linear-gradient(135deg, ${NAVY}, ${BLUE})`,
        padding: "32px 32px 26px",
        textAlign: "center"
      }}>
          <div style={{
          width: 64,
          height: 64,
          borderRadius: 18,
          background: "rgba(255,255,255,0.2)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 30,
          margin: "0 auto 14px"
        }}>
            <Building2 className="inline w-[1em] h-[1em] align-[-0.125em]" />
          </div>
          <div style={{
          fontWeight: 800,
          fontSize: 21,
          color: "#FFFFFF",
          letterSpacing: "-0.3px"
        }}>
            Hostel Login
          </div>
          <div style={{
          fontSize: 11,
          color: "rgba(255,255,255,0.85)",
          letterSpacing: "0.6px",
          textTransform: "uppercase",
          fontWeight: 500,
          marginTop: 4
        }}>
            {role ? `${HOSTELS[role].label} Portal` : "Select your hostel to continue"}
          </div>
        </div>

        <div style={{
        padding: "28px 32px 32px"
      }}>
          {/* STEP 1: Role selection cards */}
          {!role && <div style={{
          display: "flex",
          flexDirection: "column",
          gap: 14
        }}>
              {Object.entries(HOSTELS).map(([key, c]) => <button key={key} onClick={() => pickRole(key)} style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            border: `1.5px solid ${LIGHT}`,
            background: PALE,
            borderRadius: 18,
            padding: "18px 20px",
            cursor: "pointer",
            textAlign: "left"
          }}>
                  <div style={{
              width: 48,
              height: 48,
              borderRadius: 14,
              background: `linear-gradient(135deg, ${NAVY}, ${BLUE})`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 24,
              flexShrink: 0
            }}>
                    {c.icon}
                  </div>
                  <div style={{
              flex: 1
            }}>
                    <div style={{
                fontWeight: 800,
                fontSize: 15,
                color: "#1A1A1A"
              }}>{c.label}</div>
                    <div style={{
                fontSize: 12,
                color: "#607D8B",
                marginTop: 2
              }}>Warden / Staff login</div>
                  </div>
                  <div style={{
              fontSize: 20,
              color: NAVY
            }}>›</div>
                </button>)}
            </div>}

          {/* STEP 2: Login form for chosen role */}
          {role && <form onSubmit={handleLogin}>
              <button type="button" onClick={backToRoleSelect} style={{
            background: "none",
            border: "none",
            color: NAVY,
            fontSize: 12.5,
            fontWeight: 700,
            cursor: "pointer",
            padding: 0,
            marginBottom: 18,
            display: "flex",
            alignItems: "center",
            gap: 4
          }}>
                ‹ Change hostel
              </button>

              <div style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            background: PALE,
            borderRadius: 12,
            padding: "10px 14px",
            marginBottom: 22
          }}>
                <span style={{
              fontSize: 20
            }}>{HOSTELS[role].icon}</span>
                <span style={{
              fontWeight: 700,
              fontSize: 13.5,
              color: NAVY
            }}>{HOSTELS[role].label}</span>
              </div>

              <div style={{
            marginBottom: 16
          }}>
                <label style={labelStyle}>USERNAME</label>
                <input value={username} onChange={e => setUsername(e.target.value)} placeholder="Enter your warden username" style={inputStyle} autoFocus />
              </div>

              <div style={{
            marginBottom: 8
          }}>
                <label style={labelStyle}>PASSWORD</label>
                <div style={{
              position: "relative"
            }}>
                  <input type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter password" style={{
                ...inputStyle,
                paddingRight: 44
              }} />
                  <button type="button" onClick={() => setShowPassword(v => !v)} style={{
                position: "absolute",
                right: 10,
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: 15,
                color: "#90A4AE"
              }}>
                    {showPassword ? <EyeOff className="inline w-[1em] h-[1em] align-[-0.125em]" /> : ""}
                  </button>
                </div>
              </div>

              <div style={{
            fontSize: 11,
            color: "#90A4AE",
            marginBottom: 18
          }}>
                Login with the username & password created for you in Staff Management <ArrowRight className="inline w-[1em] h-[1em] align-[-0.125em]" /> Add Warden.
              </div>

              {error && <div style={{
            background: "#FFEBEE",
            color: "#C62828",
            padding: "10px 14px",
            borderRadius: 10,
            fontSize: 13,
            marginBottom: 16
          }}>
                  <AlertTriangle className="inline w-[1em] h-[1em] align-[-0.125em]" /> {error}
                </div>}

              <button type="submit" disabled={loading} style={{
            width: "100%",
            background: loading ? "#90A4AE" : `linear-gradient(135deg, ${NAVY}, ${BLUE})`,
            color: "#FFFFFF",
            border: "none",
            padding: "14px 0",
            borderRadius: 12,
            fontSize: 14,
            fontWeight: 700,
            cursor: loading ? "default" : "pointer"
          }}>
                {loading ? "Signing in..." : `Login to ${HOSTELS[role].label}`}
              </button>
            </form>}
        </div>

        {/* Gold footer accent, matches the school login styling */}
        <div style={{
        height: 5,
        background: `linear-gradient(90deg, ${GOLD}, ${GOLD_LIGHT}, ${GOLD})`
      }} />
      </div>
    </div>;
}
const labelStyle = {
  display: "block",
  fontSize: 11,
  fontWeight: 700,
  color: NAVY,
  letterSpacing: "0.5px",
  marginBottom: 6
};
const inputStyle = {
  width: "100%",
  padding: "12px 14px",
  borderRadius: 10,
  border: `1px solid ${LIGHT}`,
  fontSize: 13.5,
  color: "#1A1A1A",
  outline: "none",
  boxSizing: "border-box"
};

/*
WIRING NOTES — add these to your App.js (react-router-dom required):

  import { BrowserRouter, Routes, Route } from "react-router-dom";
  import HostelLoginPage from "./components/HostelLoginPage";
  import BoysHostelManagement from "./components/BoysHostelManagement";
  import GirlsHostelManagement from "./components/GirlsHostelManagement";

  function App() {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/hostel-login" element={<HostelLoginPage />} />
          <Route path="/hostel-boys" element={<BoysHostelManagement />} />
          <Route path="/hostel-girls" element={<GirlsHostelManagement />} />
        </Routes>
      </BrowserRouter>
    );
  }

  export default App;

Then open localhost:3000/hostel-login to test:
  1. Go to Staff Management → click "Add Warden" → create a warden with
     Hostel = "Boys Hostel" (or "Girls Hostel"), a username and a password.
  2. On /hostel-login, pick the matching hostel card, log in with that same
     username/password. Only that exact stored account works — anything else
     (wrong password, a warden from the other hostel, a non-warden staff
     username) is rejected by /api/staff/warden-login.
*/